import {CSSProperties} from 'react';

/** Tokens repris de la maquette Figma "1.1 Question Bonus". */
export const tokens = {
  borderPrimary: '#DADBDD',
  surfaceCard: '#FFFFFF',
  textBody: '#333740',
  textBodyLow: '#696D77',
  textDisable: '#B4B6BB',
  textHeading: '#262626',
  iconPrimary: '#8F9299',
  accent: '#4054CC',
  success: '#04A559',
  accentSurface: '#E8E9FB',
  surfaceDisabled: '#DADBDD',
  danger: '#E02020',
  radiusM: 8,
  radiusS: 2,
  fieldHeight: 40,
};

export const questionBonusStyles: Record<string, CSSProperties> = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    padding: 16,
  },
  pageTitle: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 8,
    margin: 0,
    fontSize: 24,
    color: tokens.textHeading,
  },
  pageTitleSuffix: {
    fontSize: 14,
    color: tokens.textBodyLow,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    padding: 24,
    backgroundColor: tokens.surfaceCard,
    borderTop: `2px solid ${tokens.borderPrimary}`,
    borderRadius: tokens.radiusS,
  },
  sectionTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: 'normal',
    color: tokens.textHeading,
  },
  sectionRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 16,
    width: '100%',
  },
  sectionColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 40,
    width: '100%',
    maxWidth: 546,
  },
  footer: {
    display: 'flex',
    gap: 8,
    paddingTop: 8,
  },
  errorMessage: {
    margin: 0,
    fontSize: 15,
    color: tokens.danger,
  },
  emptyState: {
    color: tokens.textBodyLow,
  },
};

export const selectStyles: Record<string, CSSProperties> = {
  container: {
    position: 'relative',
    display: 'flex',
    flex: '1 0 0',
    flexDirection: 'column',
    gap: 8,
    minWidth: 0,
  },
  label: {
    margin: 0,
    fontSize: 15,
    lineHeight: '20px',
    fontWeight: 500,
    color: tokens.textBodyLow,
  },
  required: {
    color: tokens.danger,
  },
  field: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    height: tokens.fieldHeight,
    padding: '8px 12px',
    backgroundColor: tokens.surfaceCard,
    border: `1px solid ${tokens.borderPrimary}`,
    borderRadius: tokens.radiusM,
    fontSize: 16,
    lineHeight: '24px',
    color: tokens.textBody,
    textAlign: 'left',
    cursor: 'pointer',
  },
  fieldDisabled: {
    backgroundColor: tokens.surfaceDisabled,
    color: tokens.textBody,
    cursor: 'not-allowed',
  },
  fieldOpen: {
    borderColor: tokens.accent,
  },
  value: {
    flex: '1 0 0',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  placeholder: {
    color: tokens.textDisable,
  },
  caret: {
    fontSize: 16,
    color: tokens.iconPrimary,
  },
  menu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    zIndex: 1000,
    margin: '4px 0 0',
    padding: 0,
    maxHeight: 240,
    overflowY: 'auto',
    listStyle: 'none',
    backgroundColor: tokens.surfaceCard,
    border: `1px solid ${tokens.borderPrimary}`,
    borderRadius: tokens.radiusM,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    fontSize: 16,
    lineHeight: '24px',
    color: tokens.textBody,
    cursor: 'pointer',
  },
  optionSelected: {
    backgroundColor: tokens.accentSurface,
    color: tokens.accent,
  },
  optionDisabled: {
    color: tokens.textDisable,
    cursor: 'not-allowed',
  },
  optionHint: {
    fontSize: 13,
    color: tokens.textBodyLow,
  },
  optionHintDisabled: {
    fontSize: 13,
    color: tokens.textDisable,
  },
  optionCheck: {
    marginLeft: 'auto',
    color: tokens.accent,
  },
  emptyOption: {
    padding: '8px 12px',
    fontSize: 16,
    color: tokens.textDisable,
  },
};

export const inputStyles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    minWidth: 0,
  },
  field: {
    width: '100%',
    height: tokens.fieldHeight,
    padding: '8px 12px',
    backgroundColor: tokens.surfaceCard,
    border: `1px solid ${tokens.borderPrimary}`,
    borderRadius: tokens.radiusM,
    fontSize: 16,
    lineHeight: '24px',
    color: tokens.textBody,
  },
  fieldInvalid: {
    borderColor: tokens.danger,
  },
  fieldDisabled: {
    backgroundColor: tokens.surfaceDisabled,
    color: tokens.textBody,
    cursor: 'not-allowed',
  },
  helper: {
    fontSize: 13,
    color: tokens.textBodyLow,
  },
  helperInvalid: {
    fontSize: 13,
    color: tokens.danger,
  },
};

export const segmentedControlStyles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    width: '100%',
  },
  track: {
    display: 'flex',
    gap: 4,
    padding: 4,
    backgroundColor: tokens.surfaceCard,
    border: `1px solid ${tokens.borderPrimary}`,
    borderRadius: tokens.radiusM,
  },
  segment: {
    flex: '1 0 0',
    minWidth: 0,
    padding: '8px 12px',
    backgroundColor: 'transparent',
    border: '1px solid transparent',
    borderRadius: tokens.radiusM,
    fontSize: 15,
    lineHeight: '20px',
    fontWeight: 500,
    color: tokens.accent,
    cursor: 'pointer',
  },
  segmentSelected: {
    backgroundColor: tokens.accent,
    borderColor: tokens.accent,
    color: '#FFFFFF',
  },
  segmentDisabled: {
    cursor: 'not-allowed',
  },
};

export const answersStyles: Record<string, CSSProperties> = {
  optionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    width: '100%',
    maxWidth: 546,
  },
  optionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 24,
  },
  optionLabel: {
    width: 64,
    fontSize: 15,
    lineHeight: '20px',
    color: tokens.textBodyLow,
  },
  equals: {
    fontSize: 16,
    color: tokens.textBodyLow,
  },
  removeButton: {
    padding: 0,
    background: 'none',
    border: 'none',
    fontSize: 16,
    color: tokens.textBodyLow,
    cursor: 'pointer',
  },
  addButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 0',
    background: 'none',
    border: 'none',
    fontSize: 15,
    lineHeight: '20px',
    fontWeight: 500,
    color: tokens.accent,
    cursor: 'pointer',
  },
  pronoContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    width: '100%',
    maxWidth: 546,
  },
  oddsTitle: {
    margin: 0,
    fontSize: 15,
    lineHeight: '20px',
    fontWeight: 500,
    color: tokens.textHeading,
  },
  oddsRow: {
    display: 'flex',
    gap: 16,
    marginTop: 8,
  },
};

export const buttonStyles: Record<string, CSSProperties> = {
  base: {
    height: 40,
    padding: '8px 16px',
    borderRadius: tokens.radiusM,
    fontSize: 15,
    lineHeight: '20px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  primary: {
    backgroundColor: tokens.accent,
    border: `1px solid ${tokens.accent}`,
    color: '#FFFFFF',
  },
  primaryDisabled: {
    backgroundColor: tokens.surfaceDisabled,
    borderColor: tokens.surfaceDisabled,
    color: '#FFFFFF',
    cursor: 'not-allowed',
  },
  secondary: {
    backgroundColor: tokens.surfaceCard,
    border: `1px solid ${tokens.accent}`,
    color: tokens.accent,
  },
  secondarySuccess: {
    backgroundColor: tokens.surfaceCard,
    border: `1px solid ${tokens.success}`,
    color: tokens.success,
  },
  secondarySuccessDisabled: {
    backgroundColor: tokens.surfaceCard,
    borderColor: tokens.surfaceDisabled,
    color: tokens.textDisable,
    cursor: 'not-allowed',
  },
  dangerOutline: {
    backgroundColor: tokens.surfaceCard,
    border: `1px solid ${tokens.danger}`,
    color: tokens.danger,
  },
  dangerOutlineDisabled: {
    backgroundColor: tokens.surfaceCard,
    borderColor: tokens.surfaceDisabled,
    color: tokens.textDisable,
    cursor: 'not-allowed',
  },
  danger: {
    backgroundColor: tokens.danger,
    border: `1px solid ${tokens.danger}`,
    color: '#FFFFFF',
  },
  dangerDisabled: {
    backgroundColor: tokens.surfaceDisabled,
    borderColor: tokens.surfaceDisabled,
    color: '#FFFFFF',
    cursor: 'not-allowed',
  },
  secondaryDisabled: {
    backgroundColor: tokens.surfaceCard,
    borderColor: tokens.surfaceDisabled,
    color: tokens.textDisable,
    cursor: 'not-allowed',
  },
};

export const modalStyles: Record<string, CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
  },
  dialog: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    width: '100%',
    maxWidth: 480,
    padding: 24,
    backgroundColor: tokens.surfaceCard,
    borderRadius: tokens.radiusM,
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.16)',
  },
  title: {
    margin: 0,
    fontSize: 18,
    fontWeight: 500,
    color: tokens.textHeading,
  },
  description: {
    margin: 0,
    fontSize: 16,
    lineHeight: '24px',
    color: tokens.textBodyLow,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
  },
  dialogDanger: {
    maxWidth: 363,
    alignItems: 'center',
    textAlign: 'center',
  },
  warningIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#FDF3E2',
    fontSize: 22,
    color: '#B8860B',
  },
  titleDanger: {
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    fontWeight: 700,
    color: '#B8860B',
  },
  descriptionDanger: {
    textAlign: 'center',
  },
  actionsDanger: {
    flexDirection: 'column-reverse',
    alignSelf: 'stretch',
    justifyContent: 'stretch',
  },
};

export const listStyles: Record<string, CSSProperties> = {
  card: {
    padding: '24px 0',
    backgroundColor: tokens.surfaceCard,
    borderRadius: tokens.radiusS,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
  },
  headerCell: {
    padding: '0 16px 24px',
    fontSize: 18,
    fontWeight: 'normal',
    color: tokens.textHeading,
    textAlign: 'left',
  },
  cell: {
    padding: '12px 16px',
    fontSize: 15,
    lineHeight: '20px',
    color: tokens.textBodyLow,
    verticalAlign: 'middle',
  },
  // Une ligne sur deux en gris.
  rowOdd: {
    backgroundColor: '#F6F7F8',
  },
  titleCell: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  actionsCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 12,
  },
  iconAction: {
    padding: 4,
    background: 'none',
    border: 'none',
    fontSize: 16,
    color: tokens.accent,
    cursor: 'pointer',
  },
  iconActionDanger: {
    color: tokens.danger,
  },
  resolveAction: {
    padding: '6px 12px',
    backgroundColor: tokens.accent,
    border: `1px solid ${tokens.accent}`,
    borderRadius: tokens.radiusM,
    fontSize: 15,
    lineHeight: '20px',
    fontWeight: 500,
    color: '#FFFFFF',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
  },
  stateMessage: {
    padding: '12px 16px',
    fontSize: 15,
    color: tokens.textBodyLow,
  },
};

export const resolveStyles: Record<string, CSSProperties> = {
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    padding: 24,
    backgroundColor: tokens.surfaceCard,
    borderRadius: tokens.radiusS,
  },
  summaryRow: {
    display: 'flex',
    gap: 40,
  },
  summaryField: {
    display: 'flex',
    flex: '1 0 0',
    flexDirection: 'column',
    gap: 4,
    minWidth: 0,
  },
  summaryLabel: {
    fontSize: 13,
    lineHeight: '18px',
    color: tokens.textBodyLow,
  },
  summaryValue: {
    fontSize: 16,
    lineHeight: '24px',
    color: tokens.textBody,
  },
  questionTitle: {
    margin: 0,
    fontSize: 20,
    lineHeight: '28px',
    fontWeight: 500,
    color: tokens.textHeading,
  },
  answerLabel: {
    fontSize: 13,
    lineHeight: '18px',
    color: tokens.textBodyLow,
  },
  optionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    width: '100%',
    maxWidth: 420,
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    width: '100%',
    padding: '10px 16px',
    backgroundColor: '#F6F7F8',
    border: `1px solid ${tokens.borderPrimary}`,
    borderRadius: tokens.radiusM,
    fontSize: 16,
    lineHeight: '24px',
    color: tokens.textBody,
    textAlign: 'left',
    cursor: 'pointer',
  },
  optionSelected: {
    backgroundColor: tokens.accentSurface,
    borderColor: tokens.accent,
  },
  radio: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: tokens.surfaceCard,
    border: `1px solid ${tokens.borderPrimary}`,
  },
  radioDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: tokens.accent,
  },
  optionText: {
    flex: '1 0 0',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  optionPoints: {
    flexShrink: 0,
    color: tokens.textBodyLow,
  },
  scoreRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  scoreSeparator: {
    fontSize: 18,
    color: tokens.textBodyLow,
  },
  backLink: {
    padding: 0,
    marginRight: 4,
    background: 'none',
    border: 'none',
    fontSize: 20,
    color: tokens.textHeading,
    cursor: 'pointer',
  },
};

export const translationsStyles: Record<string, CSSProperties> = {
  dialog: {
    maxWidth: 560,
  },
  flags: {
    display: 'flex',
    gap: 16,
  },
  flagButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    padding: '4px 4px 0',
    background: 'none',
    border: 'none',
    borderBottom: '2px solid transparent',
    fontSize: 13,
    color: tokens.textBodyLow,
    cursor: 'pointer',
  },
  flagButtonSelected: {
    borderBottomColor: tokens.accent,
    color: tokens.accent,
    fontWeight: 500,
  },
  flag: {
    width: 28,
    height: 28,
    borderRadius: 14,
  },
  fields: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  optionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  optionLabel: {
    width: 64,
    flexShrink: 0,
    fontSize: 15,
    color: tokens.textBodyLow,
  },
  ctaWithRequired: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 2,
  },
  required: {
    fontSize: 16,
    lineHeight: '16px',
    color: tokens.danger,
  },
};



// WEBPACK FOOTER //
// ./src/pages/Other/MPP/QuestionBonus/styles.ts